import { useState, useEffect } from "react";
import genRequest from "includes/request";

/**
 * Fetches a document via the API and returns an objectURL with the correct MIME type.
 * Workaround: some PDFs are stored as .jpg on Azure with wrong Content-Type.
 * This hook fetches the file content through the backend (avoids CORS),
 * detects the real type via magic number, and creates a proper blob URL.
 */
export default function usePdfBlobUrl(documentId, fallbackUrl) {
	const [blobUrl, setBlobUrl] = useState(null);
	const [isLoading, setIsLoading] = useState(true);
	const [mimeType, setMimeType] = useState(null);

	useEffect(() => {
		if (!documentId) {
			setMimeType(null);
			setBlobUrl(fallbackUrl);
			setIsLoading(false);
			return;
		}

		let objectUrl = null;

		genRequest("Documents/DocFileOfDoc/" + documentId)
			.then((base64) => {
				if (!base64) {
					setMimeType(null);
					setBlobUrl(fallbackUrl);
					setIsLoading(false);
					return;
				}

				const raw = atob(base64);
				const header = raw.substring(0, 5);
				const type = header === "%PDF-" ? "application/pdf" : "image/jpeg";

				const bytes = new Uint8Array(raw.length);
				for (let i = 0; i < raw.length; i++) {
					bytes[i] = raw.charCodeAt(i);
				}

				const blob = new Blob([bytes], { type });
				objectUrl = URL.createObjectURL(blob);
				setMimeType(type);
				setBlobUrl(objectUrl);
				setIsLoading(false);
			})
			.catch(() => {
				setMimeType(null);
				setBlobUrl(fallbackUrl);
				setIsLoading(false);
			});

		return () => {
			if (objectUrl) URL.revokeObjectURL(objectUrl);
		};
	}, [documentId, fallbackUrl]);

	// mimeType is null when the fallback url is used (real type unknown)
	return { blobUrl, isLoading, mimeType };
}
